import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth/useAuth';

/**
 * PERFORMANCE OPTIMIZED Hook for navigation redirect
 * Now uses localStorage caching to avoid expensive Firestore calls
 * Only checks preferences from cache, no blocking database queries
 * Uses app-level mounting to avoid route remount issues
 */
export const useInitialNavigationRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const hasCheckedRedirect = useRef(false);

  useEffect(() => {
    console.log('🔄 useInitialNavigationRedirect: Starting check', {
      hasCheckedRedirect: hasCheckedRedirect.current,
      isLoading,
      userExists: !!user,
      userId: user?.uid,
      currentPath: location.pathname
    });

    // PERFORMANCE: Skip all expensive checks if already done or still loading
    if (hasCheckedRedirect.current || isLoading || !user) {
      console.log('⏸️ useInitialNavigationRedirect: Skipping - already checked, loading, or no user');
      return;
    }

    // Only redirect if user is on the homepage
    if (location.pathname !== '/') {
      console.log('⏸️ useInitialNavigationRedirect: Not on homepage, skipping');
      return;
    }

    // PERFORMANCE: Check cached preference only (no Firestore calls)
    const cacheKey = `nav_pref_${user.uid}`;
    const cachedPreference = localStorage.getItem(cacheKey);
    
    console.log('💾 useInitialNavigationRedirect: Cache check', {
      cacheKey,
      cachedPreference,
      shouldRedirect: cachedPreference === 'chatbot'
    });
    
    if (cachedPreference === 'chatbot') {
      console.log('✅ useInitialNavigationRedirect: Redirecting to chatbot');
      hasCheckedRedirect.current = true;
      navigate('/chatbot', { replace: true });
      return;
    }
    
    console.log('🏠 useInitialNavigationRedirect: Staying on homepage');
    // Mark as checked to avoid future checks
    hasCheckedRedirect.current = true;
  }, [user, isLoading, location.pathname, navigate]);
};